<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { Bell, CheckCheck, Inbox } from '@lucide/vue'
import type { ShellNotification } from '../../types'

const props = defineProps<{
  notifications: ShellNotification[]
  unreadCount: number
}>()

const emit = defineEmits<{
  open: [notification: ShellNotification]
  markAllRead: []
  openCenter: []
}>()

const containerRef = ref<HTMLElement | null>(null)
const expanded = ref(false)
const latestNotifications = computed(() => props.notifications.slice(0, 5))

function openNotification(notification: ShellNotification) {
  expanded.value = false
  emit('open', notification)
}

function openCenter() {
  expanded.value = false
  emit('openCenter')
}

function handleMarkAllRead() {
  emit('markAllRead')
}

function handleDocumentClick(event: MouseEvent) {
  if (containerRef.value && !containerRef.value.contains(event.target as Node)) {
    expanded.value = false
  }
}

onMounted(() => {
  if (typeof document !== 'undefined') {
    document.addEventListener('click', handleDocumentClick)
  }
})

onBeforeUnmount(() => {
  if (typeof document !== 'undefined') {
    document.removeEventListener('click', handleDocumentClick)
  }
})

function relativeTime(value: string) {
  const seconds = Math.max(1, Math.floor((Date.now() - new Date(value).getTime()) / 1000))
  if (seconds < 60) return '刚刚'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} 分钟前`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} 小时前`
  return `${Math.floor(hours / 24)} 天前`
}
</script>

<template>
  <div ref="containerRef" class="notification-bell" :class="{ open: expanded }">
    <button
      type="button"
      class="notification-bell__trigger"
      :class="{ 'is-active': expanded }"
      :aria-expanded="expanded"
      title="消息通知"
      aria-label="打开消息通知"
      @click.stop="expanded = !expanded"
    >
      <Bell :size="16" :stroke-width="1.8" class="notification-bell__icon" aria-hidden="true" />
      <span v-if="unreadCount > 0" class="notification-bell__badge">
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>

    <div v-if="expanded" class="notification-popover" role="dialog" aria-label="消息通知面板">
      <header class="notification-popover__header">
        <div class="notification-popover__title-wrap">
          <span class="notification-popover__title">通知中心</span>
          <span v-if="unreadCount > 0" class="notification-popover__unread-tag">{{ unreadCount }} 未读</span>
        </div>
        <button
          type="button"
          class="notification-popover__mark-btn"
          :disabled="unreadCount === 0"
          title="将所有通知标记为已读"
          @click="handleMarkAllRead"
        >
          <CheckCheck :size="14" aria-hidden="true" />
          <span>全部已读</span>
        </button>
      </header>

      <div class="notification-popover__list">
        <button
          v-for="notification in latestNotifications"
          :key="notification.id"
          type="button"
          class="notification-mini"
          :class="{ unread: !notification.read }"
          @click="openNotification(notification)"
        >
          <div class="notification-mini__header">
            <span class="notification-mini__sender">{{ notification.sender || '系统' }}</span>
            <span class="notification-mini__time">{{ relativeTime(notification.createdAt) }}</span>
          </div>
          <strong class="notification-mini__title">{{ notification.title }}</strong>
          <small class="notification-mini__body">{{ notification.body || notification.linkText || '点击查看详情' }}</small>
        </button>

        <div v-if="latestNotifications.length === 0" class="notification-empty">
          <Inbox :size="28" :stroke-width="1.5" class="notification-empty__icon" aria-hidden="true" />
          <span>暂无新通知</span>
        </div>
      </div>

      <footer class="notification-popover__footer">
        <button type="button" class="notification-popover__all-btn" @click="openCenter">
          查看全部通知
        </button>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.notification-bell {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.notification-bell__trigger {
  position: relative;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  min-height: 32px !important;
  max-width: 32px !important;
  max-height: 32px !important;
  padding: 0 !important;
  margin: 0 !important;
  border: 0 !important;
  border-radius: var(--rr-radius-control, 6px);
  background: transparent !important;
  color: var(--rr-color-text, #333333) !important;
  cursor: pointer;
  line-height: 1;
  transition: all 120ms ease;
}

.notification-bell__trigger:hover,
.notification-bell__trigger.is-active {
  background: var(--rr-color-surface-subtle, rgba(0, 0, 0, 0.04)) !important;
  color: var(--rr-color-text-strong, #111111) !important;
}

.notification-bell__icon {
  display: block;
  flex-shrink: 0;
  margin: auto;
}

.notification-bell__badge {
  position: absolute;
  top: -2px;
  right: -2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  box-sizing: border-box;
  border: 2px solid var(--rr-color-surface, #ffffff);
  border-radius: 999px;
  background: var(--rr-color-danger, #ff4d4f);
  color: var(--rr-color-surface, #ffffff);
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  pointer-events: none;
}

.notification-popover {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 1200;
  width: min(340px, calc(100vw - 24px));
  overflow: hidden;
  border: 1px solid var(--rr-color-border, #e5e7eb);
  border-radius: 8px;
  background: var(--rr-color-surface, #ffffff);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
}

.notification-popover__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid var(--rr-color-border, #e5e7eb);
  background: var(--rr-color-surface, #ffffff);
}

.notification-popover__title-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}

.notification-popover__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--rr-color-text-strong, #111111);
}

.notification-popover__unread-tag {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 999px;
  background: var(--rr-color-primary-soft, #f0edff);
  color: var(--rr-color-primary, #5a46e0);
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
}

.notification-popover__mark-btn {
  display: inline-flex !important;
  align-items: center !important;
  gap: 3px;
  min-height: unset !important;
  padding: 2px 6px !important;
  margin: 0 !important;
  border: 0 !important;
  background: transparent !important;
  color: var(--rr-color-primary, #5a46e0) !important;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 4px;
  transition: background 120ms ease;
}

.notification-popover__mark-btn:hover:not(:disabled) {
  background: var(--rr-color-primary-soft, #f0edff) !important;
}

.notification-popover__mark-btn:disabled {
  color: var(--rr-color-text-subtle, #9ca3af) !important;
  cursor: not-allowed;
  opacity: 0.5;
}

.notification-popover__list {
  display: flex;
  flex-direction: column;
  max-height: 320px;
  overflow-y: auto;
}

.notification-mini {
  position: relative;
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-start !important;
  gap: 3px;
  width: 100% !important;
  min-height: unset !important;
  border: 0 !important;
  border-bottom: 1px solid var(--rr-color-border, #f3f4f6) !important;
  background: var(--rr-color-surface, #ffffff) !important;
  color: var(--rr-color-text, #374151) !important;
  padding: 10px 14px 10px 22px !important;
  margin: 0 !important;
  text-align: left !important;
  cursor: pointer;
  transition: background 120ms ease;
}

.notification-mini:last-child {
  border-bottom: 0 !important;
}

.notification-mini:hover {
  background: var(--rr-color-surface-subtle, #f9fafb) !important;
}

.notification-mini.unread {
  background: rgba(90, 70, 224, 0.03) !important;
}

.notification-mini.unread:hover {
  background: rgba(90, 70, 224, 0.07) !important;
}

.notification-mini.unread::before {
  position: absolute;
  top: 14px;
  left: 9px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--rr-color-primary, #5a46e0);
  content: "";
}

.notification-mini__header {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
}

.notification-mini__sender {
  font-size: 11px;
  font-weight: 600;
  color: var(--rr-color-text-muted, #6b7280);
}

.notification-mini__time {
  font-size: 11px;
  color: var(--rr-color-text-subtle, #9ca3af);
}

.notification-mini__title {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--rr-color-text-strong, #111111);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.notification-mini__body {
  font-size: 12px;
  color: var(--rr-color-text-muted, #6b7280);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 36px 16px;
  color: var(--rr-color-text-subtle, #9ca3af);
  font-size: 12px;
}

.notification-empty__icon {
  color: var(--rr-color-text-subtle, #cbd5e1);
}

.notification-popover__footer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  border-top: 1px solid var(--rr-color-border, #e5e7eb);
  background: var(--rr-color-surface-subtle, #f9fafb);
}

.notification-popover__all-btn {
  border: 0 !important;
  background: transparent !important;
  color: var(--rr-color-primary, #5a46e0) !important;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px !important;
  margin: 0 !important;
  min-height: unset !important;
  border-radius: 4px;
  transition: background 120ms ease;
}

.notification-popover__all-btn:hover {
  background: var(--rr-color-primary-soft, #f0edff) !important;
}
</style>
